import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Svg from 'svg'
import { useNavigation } from 'hocs'
import { useSendMollieAuthMutation } from 'settings/reduxQuery'
import { State } from 'store'
import { LoggerReject } from '../../../../../types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faForward } from '@fortawesome/pro-regular-svg-icons'
import { MollieFullIcon } from './mollieIcon'
import conviousLogo from 'images/convious-logo-pink.svg'
import { IconProp } from '@fortawesome/fontawesome'
import { Headline } from 'uiComponents/typography'
import { Col, Row } from 'uiComponents/flex'
import ActionButton from 'uiComponents/buttons'
import { UnstyledLink } from 'uiComponents/navigation/unstyledLink'
import { showErrorMessage } from 'uiComponents/message'
import { useTranslation } from 'translations'

const RedirectDefault = () => {
    const { t } = useTranslation()
    return (
        <>
            <Row align="center">
                <Col span="auto">
                    <Headline size={4}>{t('settings.accountSettings.integrations.redirect.redirecting')}</Headline>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <MollieFullIcon height="22" />
                    <span style={{ paddingLeft: '1em' }} />
                    <FontAwesomeIcon icon={faForward as IconProp} />
                    <span style={{ paddingLeft: '1em' }} />
                    <Svg src={conviousLogo} style={{ height: '1em' }} />
                </Col>
            </Row>
        </>
    )
}

const RedirectAfter10sec = () => {
    const { t } = useTranslation()
    return (
        <>
            <Row align="center">
                <Col span="auto">
                    <Headline size={4}>{t('settings.accountSettings.integrations.redirect.takesLonger')}</Headline>
                </Col>
            </Row>
            <Row align="center">
                <Col span="auto">
                    <ActionButton secondary disabled>
                        {t('settings.accountSettings.integrations.redirect.stayOnPage')}
                    </ActionButton>
                </Col>
            </Row>
        </>
    )
}

const RedirectFallback = () => {
    const { t } = useTranslation()
    const accountSlug = useSelector((state: State) => state.preferences.activeAccount)

    return (
        <>
            <Row align="center">
                <Col span="auto">
                    <Headline size={4}>{t('settings.accountSettings.integrations.redirect.takesTooLong')}</Headline>
                </Col>
            </Row>
            <Row align="center">
                <Col span="auto">
                    <UnstyledLink to={`/account/${accountSlug}/settings/account_settings/integrations`}>
                        <ActionButton kind="action">
                            {t('settings.accountSettings.integrations.redirect.goBack')}
                        </ActionButton>
                    </UnstyledLink>
                </Col>
            </Row>
        </>
    )
}

type ScreenStates = 'default' | '10sec' | 'longWaiting'

const MollieAuthCallback = () => {
    const { t } = useTranslation()
    const [sendMollieAuth] = useSendMollieAuthMutation()
    const navigation = useNavigation()
    const { state, code } = navigation.query()
    const history = useHistory()
    const accountSlug = useSelector((state: State) => state.preferences.activeAccount)
    const [renderScreen, setRenderScreen] = useState<ScreenStates>('default')

    useEffect(() => {
        if (code && accountSlug) {
            sendMollieAuth({ authorizationResponse: window.location.href, requestId: state })
                .unwrap()
                .then(() => {
                    history.push(`/account/${accountSlug}/settings/account_settings/integrations`)
                })
                .catch((err?: LoggerReject) => {
                    showErrorMessage(
                        err?.response?.error?.error || t('settings.accountSettings.integrations.mollie.genericError'),
                        'mollie_integration_error',
                    )
                })
        }
    }, [state, code, accountSlug])

    useEffect(() => {
        const firstTimeoutId = setTimeout(() => setRenderScreen('10sec'), 10e3)
        const secondTimeoutId = setTimeout(() => setRenderScreen('longWaiting'), 20e3)

        return () => {
            clearTimeout(firstTimeoutId)
            clearTimeout(secondTimeoutId)
        }
    }, [])

    const getRedirectWindow = () => {
        if (renderScreen === '10sec') {
            return <RedirectAfter10sec />
        }

        if (renderScreen === 'longWaiting') {
            return <RedirectFallback />
        }

        return <RedirectDefault />
    }

    return (
        <Row align="center" style={{ alignContent: 'center', height: '100vh', width: '100%' }}>
            <Col span="auto">{getRedirectWindow()}</Col>
        </Row>
    )
}

export default MollieAuthCallback
